import React, {useState, useEffect} from "react";
import {scaleQuantize, scaleQuantile} from 'd3-scale'; // Import scaleQuantile
import {ResponsiveChoropleth} from "@nivo/geo";
import {ResponsiveSwarmPlot} from '@nivo/swarmplot'
import {ResponsiveBar} from '@nivo/bar'
import countries from "./world_countries.json";

const urlParams = new URLSearchParams(window.location.search);
let displayType = urlParams.get('displayType') ?? 'swiss-mem-exports';

if (displayType === '') {
    displayType = 'swiss-mem-exports';
}

const colorMap = {
    'main': '#B0A49B',
    'maps': {
        'swiss-mem-exports': ['#415E88', '#8D9EB8', '#C6CEDB', '#D8E0E5'],
        'imd-world-comp-rank': ['#AF95A4', '#CFBFC8', '#E7DFE4', '#FCF3F9'],
        'product-cert': ['#F29753', '#F7C198', '#FBE0CB', '#FAE7DA']
    }
}

const customColors = colorMap['maps'][displayType] || [colorMap['maps']['swiss-mem-exports']];

const SwarmPlot = ({data, onNodeClick}) => (
    <ResponsiveSwarmPlot
        data={data}
        groups={['Ranking']}
        value="Rank"
        valueScale={{type: 'linear', min: 0, max: 67, reverse: false}}
        layout="horizontal"
        forceStrength={4}
        simulationIterations={120}
        margin={{top: 40, right: 10, bottom: 50, left: 10}}
        size={(node) => {
            return node.isHighlighted ? 30 : 12;
        }}
        colors={(node) => (node.data.isHighlighted ? colorMap['maps']['imd-world-comp-rank'][0] : colorMap['maps']['imd-world-comp-rank'][2])}
        tooltip={(node) => {
            return (
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-black-700 ring-1 ring-gray-300 ring-inset">
                    <strong>{node.data.name}</strong>:&nbsp;{node.data.Rank}
                </span>
            )
        }}
        onClick={onNodeClick}
        animate={false}
        axisLeft={false}
        axisRight={false}
    />
)

const NivoBar = ({data, keys, indexBy, title}) => {
    return (
        <div className="h-40 w-full">
            <ResponsiveBar
                data={data}
                keys={keys}
                indexBy={indexBy}
                margin={{top: 20, right: 30, bottom: 30, left: 60}}
                padding={0.3}
                layout="horizontal"
                valueScale={{type: 'linear'}}
                indexScale={{type: 'band', round: true}}
                colors={['#EDEDED', customColors[1]]}
                borderColor={{
                    from: 'color',
                    modifiers: [
                        ['darker', 1.6]
                    ]
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: title || 'Value',
                    legendPosition: 'middle',
                    legendOffset: -40,
                    format: (value) => ``
                }}
                labelTextColor={{
                    from: 'color',
                    modifiers: [
                        ['darker', 1.6]
                    ]
                }}
                label={d => `${d.value} (Billions CHF)`}
                tooltip={(node) => {
                    return (
                        <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-black-700 ring-1 ring-gray-300 ring-inset">
                            <strong>{node.id}</strong>
                        </span>
                    )
                }}
                legends={[
                    {
                        dataFrom: 'keys',
                        anchor: 'top-left',
                        direction: 'row',
                        justify: false,
                        translateX: 0,
                        translateY: -10,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemTextColor: '#484848',
                        itemOpacity: 1,
                        symbolSize: 20,
                    }
                ]}
                animate={true}
                motionConfig="gentle"
                role="application"
                ariaLabel="Bar Chart"
                groupMode='stacked'
            />
        </div>
    );
};

function MyResponsiveChoropleth() {
    const [countryData, setCountryData] = useState([]);
    const [minCountryValue, setMinCountryValue] = useState(Infinity);
    const [maxCountryValue, setMaxCountryValue] = useState(-Infinity);
    const [initialMemExportTotal, setInitialMemExportTotal] = useState(0);
    const [calculatedMemExportTotal, setCalculatedMemExportTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentCountryData, setCurrentCountryData] = useState(null);
    const [currentCountryFeature, setCurrentCountryFeature] = useState(null);

    const [projectionScale, setProjectionScale] = useState(150);
    const [projectionTranslation, setProjectionTranslation] = useState([0.5, 0.65]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setProjectionScale(100);
            } else {
                setProjectionScale(150);
            }
            setProjectionTranslation([0.5, 0.65]);
        };

        handleResize(); // Set initial values
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const unselectCountry = () => {
        setCurrentCountryData(null);
        setCurrentCountryFeature(null);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Determine if running on localhost
                const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

                const countryDataBaseUrl = isLocalhost
                    ? '//s-ge-world-map-data.test'
                    : '//s-ge.express-test.ch';

                const memExportDataBaseUrl = isLocalhost
                    ? '//s-ge-world-map-data.test'
                    : '//s-ge.express-test.ch';

                if (isLocalhost) {
                    alert("You are running on localhost. THIS WILL NOT WORK ON LOCALHOST. Please use the production URL: https://s-ge.express-test.ch/");
                }

                const countryResponse = await fetch(`${countryDataBaseUrl}/country_data.php?type=${displayType}`);
                if (!countryResponse.ok) {
                    throw new Error(`HTTP error! status: ${countryResponse.status}`);
                }
                const countryDataResult = await countryResponse.json();
                setCountryData(countryDataResult);

                const minVal = countryDataResult.reduce((min, country) => Math.min(min, country.value), Infinity);
                const maxVal = countryDataResult.reduce((max, country) => Math.max(max, country.value), -Infinity);
                setMinCountryValue(minVal);
                setMaxCountryValue(maxVal);

                const memExportResponse = await fetch(`${memExportDataBaseUrl}/mem_export_total.php`);
                if (!memExportResponse.ok) {
                    throw new Error(`HTTP error! status: ${memExportResponse.status}`);
                }
                const memExportTotalResult = await memExportResponse.json();
                setInitialMemExportTotal(memExportTotalResult);

            } catch (error) {
                setError(error);
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [displayType]);

    useEffect(() => {
        if (initialMemExportTotal !== 0 && currentCountryData) {
            const countryValueInBillions = (currentCountryData.value / 1_000_000_000).toFixed(2);
            setCalculatedMemExportTotal(parseFloat(initialMemExportTotal - countryValueInBillions).toFixed(2));
        } else if (initialMemExportTotal !== 0 && !currentCountryData) {
            setCalculatedMemExportTotal(parseFloat(initialMemExportTotal.toFixed(2)));
        }
    }, [initialMemExportTotal, currentCountryData]);


    const handleCountryClick = (feature) => {
        const selectedCountry = getSelectedCountry(feature);
        setCurrentCountryFeature(feature);
        setCurrentCountryData(selectedCountry);
    };

    const getSelectedCountry = (feature) => {
        return countryData.find(country => country.id === feature.id);
    };

    const handleSwarmPlotNodeClick = (node) => {
        const countryId = node.data.id;
        const feature = countries.features.find(f => f.id === countryId);
        if (feature) {
            handleCountryClick(feature);
        }
    };

    if (loading) {
        return <div className="rounded-md bg-blue-50 p-4">
            <div className="flex">
                <div className="ml-3 flex-1 md:flex md:justify-between">
                    <p className="text-sm text-blue-700">Loading Map...</p>
                </div>
            </div>
        </div>;
    }
    if (error) {
        return <div>Error: {error.message}</div>;
    }
    if (countryData.length === 0) {
        return <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
                <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">No country data available</h3>
                </div>
            </div>
        </div>;
    }

    // Pass countryData.map(d => d.value) to getFormattedLegendData
    const legendData = getFormattedLegendData(countryData.map(d => d.value), customColors);

    const textColor = customColors[0];
    const bgColor = customColors[3];

    return (
        <div className="relative flex flex-col items-center max-w-[900px] w-full mx-auto gap-5">
            <span className="isolate inline-flex rounded-md shadow-xs mt-3">
              <a type="button" href="/?displayType=swiss-mem-exports"
                 className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 hover:no-underline ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10">Swiss MEM Exports</a>
              <a type="button" href="/?displayType=imd-world-comp-rank"
                 className="relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 hover:no-underline ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10">IMD World Competitiveness Ranking</a>
              <a type="button" href="/?displayType=product-cert"
                 className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-[{{}}] hover:no-underline ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10">Product Certification</a>
            </span>
            <div className="nivo-chloropleth h-[500px] w-full">
                <ResponsiveChoropleth
                    data={countryData}
                    features={countries.features}
                    margin={{top: 0, right: 0, bottom: 0, left: 0}}
                    // TODO: custom colors --- Use your custom colors array here
                    colors={customColors}
                    // TODO: custom colors --- Keep domain for choropleth coloring
                    // IMPORTANT: the MAX value is set manually. This might cause issues if future datapoint exceed this value.
                    domain={[minCountryValue, 4340000000]}
                    unknownColor="#ededed"
                    projectionType="mercator"
                    projectionScale={projectionScale}
                    projectionTranslation={projectionTranslation}
                    projectionRotation={[0, 0, 0]}
                    enableGraticule={true}
                    graticuleLineColor="#ffffff"
                    borderWidth={1}
                    borderColor="#ffffff"
                    onClick={handleCountryClick}
                    legends={['', 'swiss-mem-exports'].includes(displayType) ? [
                        {
                            anchor: 'bottom-left',
                            direction: 'column',
                            justify: true,
                            translateX: 20,
                            translateY: -100,
                            itemsSpacing: 0,
                            itemWidth: 120,
                            itemHeight: 18,
                            itemDirection: 'left-to-right',
                            itemTextColor: '#484848',
                            itemOpacity: 0.85,
                            symbolSize: 18,
                            dataFrom: 'custom',
                            data: legendData, // Use the new legendData
                        }
                    ] : []}
                    tooltip={({feature}) => {
                        const selectedCountry = getSelectedCountry(feature);
                        if (!selectedCountry) return (<></>);
                        return (
                            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-black-700 ring-1 ring-gray-300 ring-inset">
                                <strong>{feature.properties.name}</strong>
                            </span>
                        );
                    }}
                />
            </div>
            <DataOverlay country={currentCountryData} feature={currentCountryFeature} memExportTotal={calculatedMemExportTotal} countryData={countryData}
                         handleSwarmPlotNodeClick={handleSwarmPlotNodeClick} unselectCountry={unselectCountry}/>
            <div className="rounded-md p-4 mb-3" style={{backgroundColor: bgColor}}>
                <div className="flex">
                    <div className="ml-3 flex-1 md:flex md:justify-between">
                        <div className="text-sm text-blue-700" style={{color: textColor}}>
                            <strong>Disclaimer 01.06.2025: </strong>
                            The information in this infographic has been gathered and researched from sources believed to be reliable and is written in good faith. Switzerland Global Enterprise cannot
                            be held liable for data that may not be complete, accurate or up-to-date, nor for data that originates from Internet sites/sources over which Switzerland Global Enterprise
                            has no control. The information in this infographic does not have a legal or juridical character, unless expressly stated.
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}

const makeLinksClickable = (text) => {
    const urlRegex = /(\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;

    return text.replace(urlRegex, (match) => {
        if (match.includes('<a target="_blank" href=') || match.includes('src=') || match.includes('data-')) {
            return match;
        }
        return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="hover:underline rounded-md focus:outline-none focus:ring-2" style="color: ${customColors[0]}">
                    ${match}
                </a>`;
    });
};

function DataOverlay({country, feature, memExportTotal, countryData, handleSwarmPlotNodeClick, unselectCountry}) {
    if (!country || !feature) {
        return null;
    }
    let html = ('');
    if (displayType === '' || displayType === null || displayType === 'swiss-mem-exports' || !displayType) {
        html = buildSwissMemExports(country, feature, memExportTotal);
    }
    if (displayType === 'imd-world-comp-rank') {
        html = buildImdWorldCompRank(country, feature, countryData, displayType, handleSwarmPlotNodeClick);
    }
    if (displayType === 'product-cert') {
        html = buildProductCert(country, feature);
    }

    const countryTitle = feature.properties.name;
    return (
        <div className="absolute inset-0 before:content-[''] before:absolute before:inset-0 before:opacity-0 before:bg-red-500" onClick={unselectCountry}>
            <div className="absolute right-5 left-5 lg:left-[35%] top-[50%] -translate-y-1/2 bg-white shadow-lg p-4 rounded-lg w-auto overflow-scroll-auto"
                 onClick={(event) => event.stopPropagation()}>
                <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white">
                    <div className="w-full flex justify-between px-4 sm:px-6" onClick={unselectCountry} title={"click to close"}>
                        <h2 className="text-gray-900 mb-0">{countryTitle}</h2>
                        <button type="button" onClick={unselectCountry}
                                className="relative rounded-md bg-white mt-[-10px] px-3.5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50">
                            <svg className="w-8 h-8" width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M10.0303 8.96965C9.73741 8.67676 9.26253 8.67676 8.96964 8.96965C8.67675 9.26255 8.67675 9.73742 8.96964 10.0303L10.9393 12L8.96966 13.9697C8.67677 14.2625 8.67677 14.7374 8.96966 15.0303C9.26255 15.3232 9.73743 15.3232 10.0303 15.0303L12 13.0607L13.9696 15.0303C14.2625 15.3232 14.7374 15.3232 15.0303 15.0303C15.3232 14.7374 15.3232 14.2625 15.0303 13.9696L13.0606 12L15.0303 10.0303C15.3232 9.73744 15.3232 9.26257 15.0303 8.96968C14.7374 8.67678 14.2625 8.67678 13.9696 8.96968L12 10.9393L10.0303 8.96965Z"
                                    fill="#1C274C"/>
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                      d="M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z"
                                      fill="#1C274C"/>
                            </svg>
                        </button>
                    </div>
                    <div className="px-4 py-5 sm:p-6">
                        {html}
                    </div>
                </div>
            </div>
        </div>
    );
}

function buildSwissMemExports(country, feature, memExportTotal) {
    if (!country || !country.data || !country.data.exportVolume) {
        return <p className="text-red-600">No export volume data available for this country.</p>;
    }

    const exportVolume = country.data.exportVolume;

    const stats = [
        {
            name: 'Top 1',
            text: exportVolume.top1Category || 'N/A'
        },
        {
            name: 'Top 2',
            text: exportVolume.top2Category || 'N/A'
        },
        {
            name: 'Top 3',
            text: exportVolume.top3Category || 'N/A'
        },
    ];

    const barChartData = [
        {
            "comparison": "",
            "Global": memExportTotal,
            [feature.properties.name]: (exportVolume.total / 1_000_000_000).toFixed(2),
        },
    ];

    const barChartKeys = ["Global", feature.properties.name];

    return (
        <>
            <h3 className="text-xl font-medium text-gray-700">MEM Export Comparison</h3>
            <div className="h-48 w-full">
                <NivoBar
                    data={barChartData}
                    keys={barChartKeys}
                    indexBy="comparison"
                    title="Export Volume"
                />
            </div>
            <h3 className="text-xl font-medium text-gray-700">Top Products</h3>
            <dl className="mt-5 grid grid-cols-1 gap-2">
                {stats.map((item) => (
                    <div key={item.name} className="overflow-hidden rounded-lg px-4 p-1 shadow-sm">
                        <dt className="truncate text-lg font-medium text-gray-500">{item.name}</dt>
                        <dd className="text-md tracking-tight text-gray-900">{item.text}</dd>
                    </div>
                ))}
            </dl>
        </>
    );
}

function buildImdWorldCompRank(country, feature, countryData, displayType, onNodeClick) {
    let data = []

    for (const item of countries.features) {
        const selectedCountry = countryData.find(country => country.id === item.id)
        if (!selectedCountry) {
            continue;
        }
        data.push({
            id: item.id,
            name: item.properties.name,
            Rank: selectedCountry?.data.easeOfDoingBusiness.overallRanking || 0,
            Ranking: 'Rank',
            isHighlighted: country.id === item.id,
        })
    }

    return (
        <>
            <h3 className="text-xl font-medium text-gray-700">IMD World Competitiveness Ranking</h3>
            <div className="h-28 w-full">
                <SwarmPlot data={data} onNodeClick={onNodeClick}/>
            </div>
            <h3 className="text-xl font-medium text-gray-700">Key MEM indicators</h3>
            <ul className="mt-5 grid grid-cols-1 gap-2">
                <div key={1} className="">
                    <strong>Business legislation:</strong> {country.data.easeOfDoingBusiness.businessLegislation}
                </div>
                <div key={2} className="">
                    <strong>Productivity & efficiency:</strong> {country.data.easeOfDoingBusiness.productivityEfficiency}
                </div>
                <div key={3} className="">
                    <strong>Technological infrastructure:</strong> {country.data.easeOfDoingBusiness.technologicalInfrastructure}
                </div>
            </ul>
            <span className="inline-flex items-center rounded-md mt-5 bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset hover:bg-gray-100">Source:&nbsp;<a
                href="https://www.imd.org/centers/wcc/world-competitiveness-center/rankings/world-competitiveness-ranking/rankings/wcr-rankings/#_tab_Rank" target="_blank"
                aria-label="Source IMD.org">www.imd.org</a></span>
        </>
    )
}

function buildProductCert(country, feature) {
    return (
        <ul className="flex flex-col gap-2">
            <li className="flex items-center gap-3">
                <div className={`w-16 flex-shrink-0 flex items-center justify-center`}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-10">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z"/>
                    </svg>
                </div>
                <div className="flex-grow min-w-0" dangerouslySetInnerHTML={{__html: makeLinksClickable(country.data.legalProvisions)}}></div>
            </li>
            <li className="flex items-center gap-3">
                <div className={`w-16 flex-shrink-0 flex items-center justify-center`}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-10">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z"/>
                    </svg>
                </div>
                <div className="flex-grow min-w-0" dangerouslySetInnerHTML={{__html: makeLinksClickable(country.data.certificationProcess)}}></div>
            </li>
            <li className="flex items-center gap-3">
                <div className={`w-16 flex-shrink-0 flex items-center justify-center`}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-10">
                        <path strokeLinecap="round" strokeLinejoin="round"
                              d="m15.75 15.75-2.489-2.489m0 0a3.375 3.375 0 1 0-4.773-4.773 3.375 3.375 0 0 0 4.774 4.774ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                    </svg>
                </div>
                <div className="flex-grow min-w-0" dangerouslySetInnerHTML={{__html: makeLinksClickable(country.data.certificationBodies)}}></div>
            </li>
        </ul>
    );
}

const getFormattedLegendData = (values, customColors) => {
    if (!values || values.length === 0) {
        return [{id: 'No Data', label: 'N/A', color: '#ededed'}];
    }

    const nivoColors = customColors;

    // Ensure values are sorted and unique for robust quantile calculation
    const uniqueSortedValues = [...new Set(values)].sort((a, b) => a - b);

    // Handle edge case where there are fewer unique values than colors
    if (uniqueSortedValues.length < nivoColors.length) {
        return uniqueSortedValues.map((value, index) => ({
            id: `value-${index}`,
            label: `${(value / 1_000_000_000).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} Billion`,
            color: nivoColors[index % nivoColors.length], // Cycle through colors
        }));
    }

    const quantileScale = scaleQuantile()
        .domain(uniqueSortedValues) // Use unique and sorted values for the domain
        .range(nivoColors); // Use the custom colors as the range

    const quantiles = quantileScale.quantiles(); // Get the quantile boundaries

    const legendItems = [];
    let previousBoundary = uniqueSortedValues[0]; // Start with the actual minimum of unique sorted values

    // Create legend items based on quantile boundaries
    for (let i = 0; i < nivoColors.length; i++) {
        // The current boundary is the i-th quantile. For the last color, use the maximum value.
        const currentBoundary = (i < quantiles.length) ? quantiles[i] : uniqueSortedValues[uniqueSortedValues.length - 1];

        const formattedStart = (previousBoundary / 1_000_000_000).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        const formattedEnd = (currentBoundary / 1_000_000_000).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});

        legendItems.push({
            id: `range-${i}`,
            label: `${formattedStart} - ${formattedEnd} Billion`,
            color: nivoColors[i],
        });
        previousBoundary = currentBoundary;
    }

    return legendItems;
};
export default MyResponsiveChoropleth;
